/**
 * What a brand's site shows for a text, in the site's own language. The site passes its section titles and link texts
 * through the platform's translation table, the text itself being the key (the site's `src/utils/i18n.ts`): playavenue
 * is a Mexican site whose sidebar names a list "Crash Games" and shows "Juegos Crash". The table is read per tenant
 * and language from the service and project the hub's own config names, which are the platform's, as the site's are.
 */

/** A translation table: a text, and what the site shows for it. */
export type SiteTranslations = Readonly<Record<string, string>>;

/** Where the site's translation table for `language` is read, or null when the config or the brand cannot say. */
export const siteTranslationsUrl = (
  config: Record<string, unknown> | undefined,
  tenantId: number | undefined,
  language: string,
): string | null => {
  const api = typeof config?.translationApiUrl === 'string' ? config.translationApiUrl.replace(/\/+$/, '') : '';
  const project = config?.translationProjectCode;
  const code = typeof project === 'string' || typeof project === 'number' ? String(project).trim() : '';
  if (!/^https:\/\/[^/]+/.test(api) || !code || !tenantId || !/^[A-Za-z]{2,3}(?:[-_][A-Za-z0-9]{2,8})?$/.test(language))
    return null;
  return `${api}/api/multilang/v1/translate/table/${encodeURIComponent(code)}/${encodeURIComponent(language)}?tenant_id=${tenantId}`;
};

/** The texts a table body translates: each one that says something, as itself. */
export const parseSiteTranslations = (body: unknown): SiteTranslations => {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) return {};
  const out: Record<string, string> = {};
  for (const [text, shown] of Object.entries(body)) if (typeof shown === 'string' && shown.trim()) out[text] = shown;
  return out;
};

/** What the site shows for `text`: its translation, else the text as it is. */
export const siteText = (table: SiteTranslations | null, text: string): string =>
  table && Object.prototype.hasOwnProperty.call(table, text) ? table[text] : text;
